import { COMPANY_ID } from "./../../../services/localstorage.service"
import { Employee, LoginInput, User } from "@finway-group/shared/lib/models"
import * as HttpStatus from "http-status"
// Imports from shared modules
import jwt from "jsonwebtoken"
// Redux imports
import { ActionCreator, Dispatch } from "redux"
import { AuthService, LocalStorageService } from "Shared/services"
import { ACCESS_TOKEN } from "Shared/services/localstorage.service"
// Model imports
// Store imports
import { ThunkResult } from "Shared/store"
import { WeavrAuthenticatedEnum, WeavrUserOnboardEnum } from "Shared/store/reducers/authReducer"
import { RootState } from "Shared/store/rootState.interface"
import { EmployeeActionTypes, UpdateEmployeeAction } from "../employee/employeeTypes"
import { FetcherActionTypes, StartLoadingAction } from "../fetcher/fetcherTypes"
// Action types' imports
import { AuthActionTypes, LoggedoutAction, LoginAction, LogoutAction, ResetAction, SetAuthUserAction, SetIsWeavrAuthenticatedAction, SetIsWeavrOnboardAction } from "./authTypes"
import { mapEmployeeActiveCompany } from "Shared/services/employee.service"

export const login = (loginInput: LoginInput) => async (dispatch: Dispatch) => {
    // prettier-ignore
    const { data: { accessToken, companyId }, status, headers } = await AuthService.login(loginInput);
    if (status === HttpStatus.PARTIAL_CONTENT) {
        throw { response: { data: {}, status, headers } }
    }

    LocalStorageService.setAccessToken(accessToken)
    // after login show loading indicator
    const setLoadingAction: StartLoadingAction = {
        type: FetcherActionTypes.START_LOADING,
        hasLoaded: false,
    }
    dispatch(setLoadingAction)

    //@todo check if we can return the auth user from the api and remove the jwt lib from the frontend
    const user: User = new User(jwt.decode(accessToken))
    const loginAction: LoginAction = {
        type: AuthActionTypes.LOGIN,
        user,
    }

    /* // this makes sure the logged in user is already in store
    // since we might retrieve it before the employee fetch
    const employee = new Employee(user);
    const syncEmployeeAction: SyncEmployeeAction = {
        type: EmployeeActionTypes.SYNC_EMPLOYEE,
        employee,
    };
    dispatch(syncEmployeeAction); */

    dispatch(loginAction)
    // TODO: fix any type
    return [user, companyId, (user as any).featureFlags]
}

export const refreshAuthUser = () => async (dispatch: Dispatch) => {
    const accessToken = LocalStorageService.get(ACCESS_TOKEN)
    if (!accessToken) return

    const user = new User(jwt.decode(accessToken))
    const setAuthUserAction: SetAuthUserAction = {
        type: AuthActionTypes.SET_AUTH_USER,
        user,
    }

    dispatch(setAuthUserAction)
}

export const logout: ActionCreator<ThunkResult<void>> = () => async (dispatch: Dispatch, getState: () => RootState) => {
    if (!getState().auth.isLoggingOut) {
        await AuthService.logout()
        const currentCompanyId = LocalStorageService.getCompanyId()
        LocalStorageService.unsetAccessToken()
        LocalStorageService.clear()

        if (currentCompanyId) {
            LocalStorageService.setCompanyId(currentCompanyId)
        }

        const logoutAction: LogoutAction = {
            type: AuthActionTypes.LOGOUT,
        }

        dispatch(logoutAction)
    }

    const loggedOutAction: LoggedoutAction = {
        type: AuthActionTypes.LOGGED_OUT,
    }

    dispatch(loggedOutAction)
    dispatch(resetAuth() as any)
}

export const resetAuth = () => async (dispatch: Dispatch) => {
    const resetAction: ResetAction = {
        type: AuthActionTypes.RESET,
    }

    dispatch(resetAction)
}

export const updatePassword = (loginInput: LoginInput) => async (dispatch: Dispatch) => {
    const { data } = await AuthService.updatePassword(loginInput)
    LocalStorageService.setAccessToken(data.accessToken)

    // @todo return user from api and delete jwt lib
    const user: User = jwt.decode(data.accessToken) as User
    const loginAction: LoginAction = {
        type: AuthActionTypes.LOGIN,
        user,
    }

    dispatch(loginAction)
    return user
}

export const resetPassword = (token: string, loginInput: LoginInput) => async (dispatch: Dispatch) => {
    const { accessToken } = await AuthService.resetPassword(token, loginInput)
    LocalStorageService.setAccessToken(accessToken)

    const user: User = jwt.decode(accessToken) as User
    const loginAction: LoginAction = {
        type: AuthActionTypes.LOGIN,
        user,
    }

    dispatch(loginAction)
    return user
}

export const setupTwoFactorAuth = (token: string) => async (dispatch: Dispatch) => {
    const {
        data: { user },
    } = await AuthService.setupTfAuth(token)

    const companyId = LocalStorageService.get(COMPANY_ID)
    const employee = new Employee(user)
    companyId && employee.setActiveCompanyProfile(companyId)

    const updateSettingsAction: UpdateEmployeeAction = {
        type: EmployeeActionTypes.UPDATE_EMPLOYEE,
        employee,
    }

    dispatch(updateSettingsAction)
    return employee
}

export const setupSMSTwoFactorAuth = () => async (dispatch: Dispatch) => {
    const {
        data: { user },
    } = await AuthService.enableSmsTfAuth()

    const companyId = LocalStorageService.get(COMPANY_ID)
    const employee = new Employee(user)
    companyId && employee.setActiveCompanyProfile(companyId)

    const updateSettingsAction: UpdateEmployeeAction = {
        type: EmployeeActionTypes.UPDATE_EMPLOYEE,
        employee,
    }

    dispatch(updateSettingsAction)
    return employee
}

export const disableTwoFactorAuth = () => async (dispatch: Dispatch) => {
    const {
        data: { user },
    } = await AuthService.disableTfAuth()

    const companyId = LocalStorageService.get(COMPANY_ID)
    const employee = new Employee(user)
    companyId && employee.setActiveCompanyProfile(companyId)

    const updateSettingsAction: UpdateEmployeeAction = {
        type: EmployeeActionTypes.UPDATE_EMPLOYEE,
        employee,
    }

    dispatch(updateSettingsAction)
    return employee
}

export const setIsWeavrAuthenticated = (isWeavrAuthenticated: WeavrAuthenticatedEnum) => async (dispatch: Dispatch) => {
    const setIsWeavrAuthenticatedAction: SetIsWeavrAuthenticatedAction = {
        type: AuthActionTypes.SET_WEAVR_AUTH,
        isWeavrAuthenticated,
    }

    dispatch(setIsWeavrAuthenticatedAction)
    return isWeavrAuthenticated
}

export const setIsWeavrUserOnboard = (isWeavrUserOnboard: WeavrUserOnboardEnum) => async (dispatch: Dispatch) => {
    const SetIsWeavrOnboardAction: SetIsWeavrOnboardAction = {
        type: AuthActionTypes.SET_WEAVR_USER_ONBOARD,
        isWeavrUserOnboard,
    }

    dispatch(SetIsWeavrOnboardAction)
    return isWeavrUserOnboard
}
